/**
 * Slug validation for store subdomains.
 * Slug must be lowercase, alphanumeric + hyphen, with length limits.
 */

const SLUG_MIN_LENGTH = 2;
const SLUG_MAX_LENGTH = 63;
const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const RESERVED_SLUGS = new Set([
  "www",
  "api",
  "app",
  "admin",
  "mail",
  "ftp",
  "staging",
  "dev",
  "development",
  "test",
  "demo",
  "help",
  "support",
  "blog",
  "docs",
  "status",
  "dashboard",
  "login",
  "signup",
  "account",
  "store",
  "upload",
  "cart",
  "product",
]);

/**
 * Parse store slug from Host header (e.g. "mysports.localhost:3000" -> "mysports").
 * Use when middleware x-store-slug is not available (e.g. some dev setups).
 */
export function getStoreSlugFromHost(
  host: string,
  baseDomain: string = process.env.NEXT_PUBLIC_APP_DOMAIN ?? "localhost"
): string | null {
  const hostLower = host.toLowerCase().split(":")[0];
  if (hostLower === baseDomain || hostLower === `www.${baseDomain}`) {
    return null;
  }
  if (baseDomain === "localhost") {
    const parts = hostLower.split(".");
    if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
      const sub = parts.slice(0, -1).join(".");
      if (sub && !RESERVED_SLUGS.has(sub)) return sub;
    }
    return null;
  }
  const suffix = `.${baseDomain}`;
  if (hostLower.endsWith(suffix)) {
    const sub = hostLower.slice(0, -suffix.length);
    if (sub && !RESERVED_SLUGS.has(sub)) return sub;
  }
  return null;
}

export type SlugValidationResult =
  | { valid: true }
  | { valid: false; error: string };

export function validateSlug(slug: string): SlugValidationResult {
  const trimmed = slug.trim().toLowerCase();
  if (!trimmed) {
    return { valid: false, error: "Store URL is required." };
  }
  if (trimmed.length < SLUG_MIN_LENGTH) {
    return { valid: false, error: `Store URL must be at least ${SLUG_MIN_LENGTH} characters.` };
  }
  if (trimmed.length > SLUG_MAX_LENGTH) {
    return { valid: false, error: `Store URL must be at most ${SLUG_MAX_LENGTH} characters.` };
  }
  if (!SLUG_REGEX.test(trimmed)) {
    return {
      valid: false,
      error: "Use only lowercase letters, numbers, and hyphens (e.g. my-store).",
    };
  }
  if (RESERVED_SLUGS.has(trimmed)) {
    return { valid: false, error: `"${trimmed}" is reserved.` };
  }
  return { valid: true };
}

/** Normalize slug for storage: lowercase, trim. Assumes validateSlug already passed. */
export function normalizeSlug(slug: string): string {
  return slug.trim().toLowerCase();
}
